import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { legalDocumentsApi } from '@/lib/pillarApi';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import type { CreateLegalDocumentRequest } from '@anchorbase/pillar-sdk';

type EditorMode = 'paste' | 'upload';

const DateTimeLocalSchema = z.string().min(1);

function toIsoFromDatetimeLocal(value: string): string {
    const parseResult = DateTimeLocalSchema.safeParse(value);
    if (!parseResult.success) {
        throw new Error('Datetime is required');
    }
    const d = new Date(parseResult.data);
    if (Number.isNaN(d.getTime())) {
        throw new Error('Invalid datetime value');
    }
    return d.toISOString();
}

export function LegalDocumentCreatePage() {
    const breadcrumbNavigate = useBreadcrumbNavigation();

    const [title, setTitle] = useState<string>('');
    const [version, setVersion] = useState<string>('');
    const [contentType, setContentType] = useState<string>('text/html');
    const [inForceSince, setInForceSince] = useState<string>('');
    const [inForceTo, setInForceTo] = useState<string>('');
    const [requiredForUsers, setRequiredForUsers] = useState<boolean>(true);
    const [requiredForOrgs, setRequiredForOrgs] = useState<boolean>(false);
    const [editorMode, setEditorMode] = useState<EditorMode>('paste');
    const [text, setText] = useState<string>('');
    const [sourceFilename, setSourceFilename] = useState<string | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const createMutation = useMutation({
        mutationFn: async (data: CreateLegalDocumentRequest) => {
            return await legalDocumentsApi.createLegalDocument(data);
        },
        onSuccess: (result) => {
            breadcrumbNavigate({
                source: 'terminal',
                target: {
                    url: `/legal-documents/${result.legal_document_uuid}`,
                    title: 'Legal Document Details',
                },
            });
        },
        onError: (err) => {
            setErrorMessage(err instanceof Error ? err.message : 'Create failed');
        },
    });

    const missingRequiredFields: ReadonlyArray<string> = useMemo(() => {
        const missing: Array<string> = [];
        if (title.trim().length === 0) missing.push('Title');
        if (version.trim().length === 0) missing.push('Version');
        if (contentType.trim().length === 0) missing.push('Content type');
        if (inForceSince.trim().length === 0) missing.push('In force since');
        if (text.trim().length === 0) missing.push('Text');
        return missing;
    }, [contentType, inForceSince, inForceTo, text, title, version]);

    const canSubmit: boolean = useMemo(() => {
        if (createMutation.isPending) {
            return false;
        }
        return missingRequiredFields.length === 0;
    }, [createMutation.isPending, missingRequiredFields.length]);

    const onUploadFileSelected = async (file: File): Promise<void> => {
        setSourceFilename(file.name);
        const fileText: string = await file.text();
        setText(fileText);
    };

    return (
        <div className="p-6 max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold">Create Legal Document</h1>
                <Button
                    variant="outline"
                    onClick={() => {
                        breadcrumbNavigate({
                            source: 'panel',
                            target: {
                                url: '/legal-documents',
                                title: 'Legal Documents',
                            },
                        });
                    }}
                >
                    Back
                </Button>
            </div>

            {errorMessage && (
                <Card className="mb-4 border-red-300">
                    <CardHeader>
                        <CardTitle className="text-red-700">Error</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm">{errorMessage}</p>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Metadata</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label>Title</Label>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Version</Label>
                        <Input value={version} onChange={(e) => setVersion(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Content type</Label>
                        <Input
                            value={contentType}
                            onChange={(e) => setContentType(e.target.value)}
                            placeholder="text/html"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>In force since</Label>
                            <Input
                                type="datetime-local"
                                value={inForceSince}
                                onChange={(e) => setInForceSince(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>In force to</Label>
                            <Input
                                type="datetime-local"
                                value={inForceTo}
                                onChange={(e) => setInForceTo(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <Checkbox
                            checked={requiredForUsers}
                            onCheckedChange={(checked) => setRequiredForUsers(checked === true)}
                        />
                        <Label>Required for users</Label>
                    </div>
                    <div className="flex items-center gap-3">
                        <Checkbox
                            checked={requiredForOrgs}
                            onCheckedChange={(checked) => setRequiredForOrgs(checked === true)}
                        />
                        <Label>Required for organizations</Label>
                    </div>
                </CardContent>
            </Card>

            <Card className="mt-4">
                <CardHeader>
                    <CardTitle className="text-base">Content</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-4 text-sm">
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                checked={editorMode === 'paste'}
                                onChange={() => setEditorMode('paste')}
                            />
                            Paste
                        </label>
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                checked={editorMode === 'upload'}
                                onChange={() => setEditorMode('upload')}
                            />
                            Upload
                        </label>
                    </div>

                    {editorMode === 'upload' ? (
                        <div className="space-y-2">
                            <Label>Upload file</Label>
                            <Input
                                type="file"
                                onChange={(e) => {
                                    const file: File | null = e.target.files?.item(0) ?? null;
                                    if (!file) {
                                        return;
                                    }
                                    onUploadFileSelected(file).catch((err) => {
                                        setErrorMessage(err instanceof Error ? err.message : 'File read failed');
                                    });
                                }}
                            />
                        </div>
                    ) : null}

                    <div className="space-y-2">
                        <Label>Text</Label>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            className="w-full h-[40vh] border rounded p-2 font-mono text-sm"
                        />
                    </div>
                </CardContent>
            </Card>

            <div className="mt-4 flex items-center justify-end gap-3">
                {!canSubmit && missingRequiredFields.length > 0 && (
                    <div className="mr-auto text-sm text-gray-600">
                        Required: {missingRequiredFields.join(', ')}
                    </div>
                )}
                <Button
                    disabled={!canSubmit}
                    onClick={() => {
                        setErrorMessage(null);
                        const payload: CreateLegalDocumentRequest = {
                            legal_document_title: title.trim(),
                            legal_document_version: version.trim(),
                            legal_document_text: text,
                            legal_document_content_type: contentType.trim(),
                            in_force_start: toIsoFromDatetimeLocal(inForceSince),
                            in_force_end:
                                inForceTo.trim().length === 0
                                    ? null
                                    : toIsoFromDatetimeLocal(inForceTo),
                            required_for_users: requiredForUsers,
                            required_for_orgs: requiredForOrgs,
                            source_filename: sourceFilename,
                        };
                        createMutation.mutate(payload);
                    }}
                >
                    {createMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
            </div>
        </div>
    );
}
